import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Users, Trash2, ChevronDown, ChevronUp, User, FileText } from 'lucide-react';
import { toast } from 'sonner';
import CityCombobox from '@/components/forms/CityCombobox';
import { INDIAN_PASSPORT_CITIES } from '@/data/indianPassportCities';
import PassportOCRButton from './PassportOCRButton';
import { extractOcrName } from '@/utils/ocrFields';

const RELATIONSHIPS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'];

const createEmptyMember = () => ({
  _id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  relationship: '',
  fullName: '',
  dateOfBirth: '',
  gender: '',
  passportNumber: '',
  passportIssueDate: '',
  passportExpiryDate: '',
  passportIssuedAt: '',
  panCard: '',
  aadharCard: ''
});

export const FamilyTab = ({ formData, setFormData, ucc }) => {
  const [expanded, setExpanded] = useState({});

  // Backfill a stable _id on any legacy members loaded from the DB without one,
  // otherwise editing them will trigger the same "only-1-letter-then-unfocus"
  // remount bug that affected newly added members.
  React.useEffect(() => {
    const list = formData?.familyMembers;
    if (!Array.isArray(list) || list.length === 0) return;
    const needsBackfill = list.some((m) => !m || !m._id);
    if (!needsBackfill) return;
    setFormData((prev) => ({
      ...prev,
      familyMembers: (prev.familyMembers || []).map((m, i) =>
        m?._id ? m : { ...(m || {}), _id: `m_legacy_${i}_${Date.now().toString(36)}` }
      ),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.familyMembers?.length]);

  const addMember = () => {
    const newMembers = [...formData.familyMembers, createEmptyMember()];
    setFormData(prev => ({ ...prev, familyMembers: newMembers }));
    setExpanded(prev => ({ ...prev, [newMembers.length - 1]: true }));
  };

  const removeMember = (idx) => {
    setFormData(prev => ({
      ...prev,
      familyMembers: prev.familyMembers.filter((_, i) => i !== idx)
    }));
  };

  const updateMember = (idx, field, value) => {
    const updated = formData.familyMembers.map((m, i) => 
      i === idx ? { ...m, [field]: value } : m
    );
    setFormData(prev => ({ ...prev, familyMembers: updated }));
  };

  const toggleExpand = (idx) => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  const members = formData.familyMembers || [];

  return (
    <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">Family Tree</h2>
          <p className="text-slate-500 text-sm mt-1">Add family members and their travel documents</p>
        </div>
        <Button type="button" onClick={addMember} data-testid="add-family-member-btn" className="bg-gradient-to-br from-primary-700 to-primary-600 text-white rounded-xl px-6">
          <Plus className="h-4 w-4 mr-2" />Add Family Member
        </Button>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">No family members added yet</p>
          <p className="text-slate-400 text-sm mt-1">Click "Add Family Member" to add dependents</p>
        </div>
      ) : (
        <div className="space-y-4">
          {members.map((member, idx) => (
            <MemberCard
              key={member._id || `m-${idx}`}
              member={member}
              idx={idx}
              isExpanded={expanded[idx]}
              onToggle={() => toggleExpand(idx)}
              onRemove={() => removeMember(idx)}
              onUpdate={(field, value) => updateMember(idx, field, value)}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

const MemberCard = ({ member, idx, isExpanded, onToggle, onRemove, onUpdate }) => {
  const handlePassportExtract = useCallback((extracted) => {
    // Only fill empty fields so manual edits aren't overwritten
    const setIfEmpty = (field, value) => {
      if (!value) return;
      if ((member?.[field] || '').toString().trim()) return;
      onUpdate(field, value);
    };
    setIfEmpty('fullName', extractOcrName(extracted));
    setIfEmpty('dateOfBirth', extracted.dateOfBirth);
    setIfEmpty('passportNumber', extracted.passportNumber);
    setIfEmpty('passportIssueDate', extracted.passportIssueDate);
    setIfEmpty('passportExpiryDate', extracted.passportExpiryDate);
    setIfEmpty('passportIssuedAt', extracted.passportIssuedAt);
    if (extracted.gender) {
      const g = extracted.gender.trim().toLowerCase();
      const norm = g.startsWith('m') ? 'Male' : g.startsWith('f') ? 'Female' : '';
      if (norm) setIfEmpty('gender', norm);
    }
  }, [member, onUpdate]);

  return (
    <div className="border-2 border-slate-100 rounded-2xl overflow-hidden bg-white">
      <div 
        className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center">
            <User className="h-5 w-5 text-primary-700" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">{member.fullName || `Family Member ${idx + 1}`}</p>
            <p className="text-sm text-slate-500">{member.relationship || 'Relationship not set'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            data-testid={`remove-family-member-${idx}`}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </div>
      </div>

      {isExpanded && (
        <div className="p-6 space-y-6 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Relationship *</Label>
              <Select value={member.relationship} onValueChange={(v) => onUpdate('relationship', v)}>
                <SelectTrigger data-testid={`family-relationship-${idx}`} className="h-11 rounded-xl">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Full Name *</Label>
              <Input 
                value={member.fullName} 
                onChange={(e) => onUpdate('fullName', e.target.value)}
                data-testid={`family-name-${idx}`}
                className="h-11 rounded-xl" 
                placeholder="Full name"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Gender</Label>
              <Select value={member.gender} onValueChange={(v) => onUpdate('gender', v)}>
                <SelectTrigger data-testid={`family-gender-${idx}`} className="h-11 rounded-xl">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Date of Birth</Label>
              <Input 
                type="date"
                value={member.dateOfBirth} 
                onChange={(e) => onUpdate('dateOfBirth', e.target.value)}
                data-testid={`family-dob-${idx}`}
                className="h-11 rounded-xl"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4" /> Travel Documents
            </h4>

            <div className="mb-4">
              <PassportOCRButton
                onExtract={handlePassportExtract}
                docType="passport"
                label={`Scan Passport for ${member.fullName || `Member ${idx + 1}`}`}
                size="sm"
                variant="outline"
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                testId={`family-scan-passport-${idx}`}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Passport Number</Label>
                <Input 
                  value={member.passportNumber} 
                  onChange={(e) => onUpdate('passportNumber', e.target.value)}
                  data-testid={`family-passport-${idx}`}
                  className="h-10 rounded-lg text-sm" 
                  placeholder="P1234567"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Passport Issued At</Label>
                <CityCombobox
                  value={member.passportIssuedAt || ''}
                  onChange={(v) => onUpdate('passportIssuedAt', v)}
                  options={INDIAN_PASSPORT_CITIES}
                  placeholder="Type or pick PSK city"
                  testId={`family-passport-city-${idx}`}
                  className="h-10 rounded-lg text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Passport Issue Date</Label>
                <Input 
                  type="date"
                  value={member.passportIssueDate} 
                  onChange={(e) => onUpdate('passportIssueDate', e.target.value)}
                  data-testid={`family-passport-issue-${idx}`}
                  className="h-10 rounded-lg text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Passport Expiry Date</Label>
                <Input 
                  type="date"
                  value={member.passportExpiryDate} 
                  onChange={(e) => onUpdate('passportExpiryDate', e.target.value)}
                  data-testid={`family-passport-expiry-${idx}`}
                  className="h-10 rounded-lg text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">PAN Card</Label>
                <Input 
                  value={member.panCard} 
                  onChange={(e) => onUpdate('panCard', e.target.value)}
                  data-testid={`family-pan-${idx}`}
                  className="h-10 rounded-lg text-sm" 
                  placeholder="ABCDE1234F"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Aadhar Card</Label>
                <Input 
                  value={member.aadharCard} 
                  onChange={(e) => onUpdate('aadharCard', e.target.value)}
                  data-testid={`family-aadhar-${idx}`}
                  className="h-10 rounded-lg text-sm" 
                  placeholder="1234-5678-9012"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
